import {
  CustomerInformationQuery_ConsumerInterface,
  CustomerInformation_Address_Response,
  genericBeforeValidate_removeDefaultKeyedError,
  GenericForm,
  GenericFormLogicalField,
  GenericFormValidation_YieldEvent,
  GenericTextFieldValidationOptions,
  ValidationEvent,
} from "../../../Common";

import { DataMutableSourceShippingClientStorageDataConsumerInterface } from "../data-mutable-source-shipping-client-storage-data-provider";
import { ReactMyBagLoginAppConsumer } from "../react-my-bag-login-app-provider";

export interface AppMinimumRequirementsFor_SavedAddress {
  reactMyBagLoginApp: ReactMyBagLoginAppConsumer;
  customerInformation?: CustomerInformationQuery_ConsumerInterface;
  shippingClientStorageData: DataMutableSourceShippingClientStorageDataConsumerInterface;
}

interface SavedAddressFormLogicProps<
  Form extends GenericForm,
  App extends AppMinimumRequirementsFor_SavedAddress
> {
  required?: boolean;
  guard?: GenericTextFieldValidationOptions<Form, App>["guard"];
}

export enum FormFieldExtensionKeys_SavedAddress {
  IsSavedAddress = "_isSavedAddress",
  HasMigratedClientStorageDataToTheInMemoryForm = "_hasMigratedClientStorageDataToTheInMemoryForm",
  HasMigratedNewUserLogInToTheInMemoryForm = "_hasMigratedNewUserLogInToTheInMemoryForm",
}

export const savedAddressFormLogic = <
  Form extends GenericForm,
  App extends AppMinimumRequirementsFor_SavedAddress
>(
  props: SavedAddressFormLogicProps<Form, App> = {}
): GenericFormLogicalField<Form, App> => ({
  presentation: props,

  produceEffectDependencies: (context) => [
    context.form[
      `${context.name}${FormFieldExtensionKeys_SavedAddress.HasMigratedClientStorageDataToTheInMemoryForm}`
    ],
    context.form[
      `${context.name}${FormFieldExtensionKeys_SavedAddress.HasMigratedNewUserLogInToTheInMemoryForm}`
    ],
    context.app.shippingClientStorageData.hasInitialized,
    context.app.shippingClientStorageData.lastSelectedSavedAddressId,
    context.app.customerInformation,
    context.app.customerInformation?.status,
    context.app.customerInformation?.data,
    context.app.customerInformation?.data?.addresses,
  ],
  handleEffect: (context) => {
    const thisFieldIsland = context.name.substring(
      0,
      context.name.indexOf("_")
    );

    const {
      hasInitialized: shippingClientStorageData_hasInitialized,
      lastSelectedSavedAddressId,
    } = context.app.shippingClientStorageData;

    const {
      status: customerInformationQueryStatus,
      data: customerInformationData,
    } = context.app.customerInformation || {};
    const customerInformation_hasInitialized =
      customerInformationQueryStatus === "success" ||
      customerInformationQueryStatus === "error";

    const {
      hasTheUserLoggedInWhileUsingThisPage_andWereInitiallyNotLoggedInToStartWith,
      userLoggedInWithThisEmailLastIHeard,
    } = context.app.reactMyBagLoginApp;

    const hasInitialized =
      shippingClientStorageData_hasInitialized &&
      customerInformation_hasInitialized;
    const hasMigratedClientStorageDataToTheInMemoryForm = context.form[
      `${context.name}${FormFieldExtensionKeys_SavedAddress.HasMigratedClientStorageDataToTheInMemoryForm}`
    ] as boolean;
    const hasMigratedNewUserLogInToTheInMemoryForm = context.form[
      `${context.name}${FormFieldExtensionKeys_SavedAddress.HasMigratedNewUserLogInToTheInMemoryForm}`
    ] as boolean;
    const theCustomerInformationHasLoadedForThisNewLoggedInUser_insteadOfWhateverStaleWasTherePreexisting =
      userLoggedInWithThisEmailLastIHeard &&
      customerInformationData?.email === userLoggedInWithThisEmailLastIHeard;

    const currentlySelectedAddressId = context.form[context.name];
    const thereIsCurrentlyAValidSavedAddress =
      currentlySelectedAddressId &&
      (customerInformationData?.addresses || []).some(
        (savedAddress) => savedAddress.id === currentlySelectedAddressId
      );

    const needsAFirstTimeInitialization =
      !hasMigratedClientStorageDataToTheInMemoryForm && hasInitialized;
    const needsANewInitializationBecauseTheUserLoggedIn =
      !!hasTheUserLoggedInWhileUsingThisPage_andWereInitiallyNotLoggedInToStartWith &&
      hasInitialized &&
      theCustomerInformationHasLoadedForThisNewLoggedInUser_insteadOfWhateverStaleWasTherePreexisting &&
      !hasMigratedNewUserLogInToTheInMemoryForm;
    const runInitializationForALoggedInUser =
      needsAFirstTimeInitialization ||
      needsANewInitializationBecauseTheUserLoggedIn;
    const iHaveInitializedAlready = !runInitializationForALoggedInUser;
    if (runInitializationForALoggedInUser) {
      if (lastSelectedSavedAddressId) {
        const yield_setSavedAddressId: GenericFormValidation_YieldEvent = {
          name: context.name,
          validationEvent: ValidationEvent.onChange,
          value: lastSelectedSavedAddressId,
        };
        context.yield.validationEvent(context, yield_setSavedAddressId);
      } else {
        let firstSavedAddress: CustomerInformation_Address_Response | null =
          null;
        const savedAddresses = customerInformationData?.addresses || [];
        if (savedAddresses.length > 0) {
          firstSavedAddress = savedAddresses[0];
        }

        let firstSavedAddressId;
        if (firstSavedAddress) {
          firstSavedAddressId = firstSavedAddress.id;
        }

        if (firstSavedAddressId) {
          const yield_setSavedAddressId: GenericFormValidation_YieldEvent = {
            name: context.name,
            validationEvent: ValidationEvent.onChange,
            value: firstSavedAddressId,
          };
          context.yield.validationEvent(context, yield_setSavedAddressId);
        }
      }

      const savedAddresses = customerInformationData?.addresses || [];
      if (savedAddresses.length > 0) {
        const yield_setIsSavedAddress_enabledByDefault_becauseThereAreSavedAddresses: GenericFormValidation_YieldEvent =
          {
            name: `${thisFieldIsland}${FormFieldExtensionKeys_SavedAddress.IsSavedAddress}`,
            validationEvent: ValidationEvent.onChange,
            value: true,
          };
        context.yield.validationEvent(
          context,
          yield_setIsSavedAddress_enabledByDefault_becauseThereAreSavedAddresses
        );
      }

      const yield_setHasMigratedClientStorageDataToTheInMemoryForm: GenericFormValidation_YieldEvent =
        {
          name: `${context.name}${FormFieldExtensionKeys_SavedAddress.HasMigratedClientStorageDataToTheInMemoryForm}`,
          validationEvent: ValidationEvent.onChange,
          value: true,
        };
      context.yield.validationEvent(
        context,
        yield_setHasMigratedClientStorageDataToTheInMemoryForm
      );
    }
    if (needsANewInitializationBecauseTheUserLoggedIn) {
      const yield_setHasMigratedNewUserLogInToTheInMemoryForm: GenericFormValidation_YieldEvent =
        {
          name: `${context.name}${FormFieldExtensionKeys_SavedAddress.HasMigratedNewUserLogInToTheInMemoryForm}`,
          validationEvent: ValidationEvent.onChange,
          value: true,
        };
      context.yield.validationEvent(
        context,
        yield_setHasMigratedNewUserLogInToTheInMemoryForm
      );
    }

    if (iHaveInitializedAlready && !thereIsCurrentlyAValidSavedAddress) {
      let firstSavedAddress: CustomerInformation_Address_Response | null = null;
      const savedAddresses = customerInformationData?.addresses || [];
      if (savedAddresses.length > 0) {
        firstSavedAddress = savedAddresses[0];
      }

      let firstSavedAddressId;
      if (firstSavedAddress) {
        firstSavedAddressId = firstSavedAddress.id;
      }

      if (firstSavedAddressId) {
        const yield_setSavedAddressId: GenericFormValidation_YieldEvent = {
          name: context.name,
          validationEvent: ValidationEvent.onChange,
          value: firstSavedAddressId,
        };
        context.yield.validationEvent(context, yield_setSavedAddressId);
      }
    }
  },

  validationEvents: [ValidationEvent.onSubmit],
  beforeValidate: genericBeforeValidate_removeDefaultKeyedError,
  validate: (context) => {
    if (props.guard && !props.guard(context)) {
      return;
    }

    const thisSelectedSavedAddressId = context.form[context.name];
    if (!thisSelectedSavedAddressId) {
      const partialForm = {
        [context.name]: "",
        [`${context.name}Error`]: "This field is required.",
      } as Partial<Form>;
      context = context.mutate.withPartialForm(context, partialForm);
    }
  },
});
